import { useEffect, useRef, useState } from 'react';
import Field from '../../components/common/Field';
import useEventsStore from '../../store/useEventsStore';
import { todayISO } from '../../utils/formatters';
import { reasonTagOptions, reasonTagLabel } from '../../utils/constants';

/* openCount bumps every time the form is (re)opened: fields reset to defaults,
   or to `editing`'s values when updating an existing expense.
   onSave(payload) → true closes; payload.receiptFile is a File only when a new
   receipt was chosen (the API replaces the stored receipt URL with it). */
export default function ExpenseForm({ open, openCount, onClose, editing, onSave }) {
  const events = useEventsStore((s) => s.events);

  const [tag, setTag] = useState('supplies');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [desc, setDesc] = useState('');
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const reasons = reasonTagOptions(events);

  useEffect(() => {
    if (openCount > 0) {
      if (editing) {
        /* API stores category as a plain label — map back to our tag options */
        const rsn = reasons.find((t) => t.label.toLowerCase() === String(editing.category || '').toLowerCase());
        setTag(rsn ? rsn.id : 'other');
        setAmount(editing.amount ? String(editing.amount) : '');
        setDate(editing.date || '');
        setDesc(editing.desc || '');
        setReceiptPreview(editing.receipt || null);
      } else {
        setTag('supplies');
        setAmount('');
        setDate('');
        setDesc('');
        setReceiptPreview(null);
      }
      setReceiptFile(null);
      if (fileRef.current) fileRef.current.value = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  const readFile = (file) => {
    if (!file) return;
    setReceiptFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setReceiptPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const reset = () => {
    setDesc('');
    setAmount('');
    setDate('');
    setReceiptPreview(null);
    setReceiptFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const save = async () => {
    const amountNum = parseFloat(amount);
    if (!desc.trim()) { window.alert('Add a short description of what it was used for.'); return; }
    if (!amountNum || amountNum <= 0) { window.alert('Enter an amount greater than $0.'); return; }
    setSaving(true);
    const ok = await onSave({
      category: reasonTagLabel(tag, events),
      amount: amountNum,
      dateSpent: date || todayISO(),
      usedFor: desc.trim(),
      receiptFile,
    });
    setSaving(false);
    if (ok) reset();
  };

  return (
    <div className={`inline-form ${open ? 'on' : ''}`}>
      <div className="form-grid">
        <Field label="Category" style={{ gridColumn: '1/-1' }}>
          <select value={tag} onChange={(e) => setTag(e.target.value)}>
            {reasons.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Amount Spent ($)">
          <input type="number" value={amount} placeholder="0.00" step="0.01" min="0" onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Date Spent">
          <div className="date-input-wrap">
            <svg className="cal-ic" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </Field>
        <Field label="What it was used for" style={{ gridColumn: '1/-1' }}>
          <input type="text" value={desc} placeholder="e.g. Spirit Night snacks" onChange={(e) => setDesc(e.target.value)} />
        </Field>
      </div>
      <Field label="Receipt (optional)" style={{ marginBottom: 10 }}>
        <div
          className={`dropzone ${dragging ? 'drag' : ''} ${receiptPreview ? 'has-file' : ''}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); readFile(e.dataTransfer.files[0]); }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => readFile(e.target.files && e.target.files[0])}
          />
          <div className="dropzone-inner">
            {receiptPreview ? (
              <>
                <img className="dropzone-preview" src={receiptPreview} alt="Receipt" />
                <span className="dropzone-text">Click to change</span>
              </>
            ) : (
              <>
                <span className="dropzone-icon"><img src="/assets/icons/upload.svg" alt="" /></span>
                <span className="dropzone-text">Click to upload, or drag a photo of the receipt here</span>
              </>
            )}
          </div>
        </div>
      </Field>
      <div className="inline-form-actions">
        <button className="btn btn-ghost" onClick={close}>Cancel</button>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : editing ? 'Update Expense' : 'Save Expense'}
        </button>
      </div>
    </div>
  );
}
